import type { IAuthTokens, IUser } from '@futmanager/api-sdk'
import { FutManagerApi } from '@futmanager/api-sdk'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { normalizeAdminUser } from '~/utils/normalize-admin-user'

const ACCESS_TOKEN_KEY = 'fmtoken'
const REFRESH_TOKEN_KEY = 'fmrefresh'
const EXPIRES_AT_KEY = 'fmexpires_at'

export const useUserStore = defineStore('user', () => {
  const router = useRouter()

  const user = ref<IUser>()

  function persistTokens(tokens: Pick<IAuthTokens, 'token' | 'refresh_token' | 'expires_in'>) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.token)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
    localStorage.setItem(
      EXPIRES_AT_KEY,
      String(Date.now() + tokens.expires_in * 1000),
    )
    FutManagerApi.setAuthToken(tokens.token)
  }

  /** Persist access + refresh pair from `/login` or `/login/refresh`. */
  function setSession(tokens: IAuthTokens) {
    persistTokens(tokens)
    setUser(tokens.user)
  }

  /** @deprecated Prefer `setSession` when refresh_token is available. */
  function setToken(token: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
    FutManagerApi.setAuthToken(token)
  }

  function setUser(u: IUser | Partial<IUser> | null | undefined) {
    const normalized = normalizeAdminUser(u ?? undefined)
    if (!normalized) {
      localStorage.removeItem('user')
      user.value = undefined
      return
    }
    localStorage.setItem('user', JSON.stringify(normalized))
    user.value = normalized
  }

  function clearSessionStorage() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(EXPIRES_AT_KEY)
    localStorage.removeItem('user')
  }

  function logout() {
    clearSessionStorage()
    FutManagerApi.removeAuthToken()
    user.value = undefined
    router.push('/')
  }

  return {
    clearSessionStorage,
    logout,
    persistTokens,
    setSession,
    setToken,
    setUser,
    user,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
